/**
 * Endereços › Sugestões (D704, doc 28 gap G6) - framework-neutral view-models over `/v1/admin/places/feedback`: the queue of what riders suggested ("Sugerir um local")
 * or corrected ("Corrigir este local") from the address search, worked by an operator who accepts it (a new place with source `rider_suggestion`, or the correction
 * applied to the base place, or a closed place hidden), merges it into a place the base already has, or rejects it with a reason - and the riders' comments about the
 * search ("Outros comentários"), which are only marked handled or rejected (they never reach the base).
 *
 *  - the query a scope and the status / kind filters send (the top scope selector is the one geography control, like the Endereços list);
 *  - what a row reads (the suggestion's headline, the base place it is about);
 *  - the accept form: what the operator may adjust (name, address, kind, the point - required when the rider pinned none), the minimal body, and whether the
 *    verified-place protection asks for the explicit override.
 *
 * The console only presents: the server authorizes every call at the geography of the suggestion and validates every value again. Neutral: no country, city or language.
 */
import type { AdminPlaceFeedbackAccept, AdminPlaceFeedbackQuery, AdminPlaceFeedbackView, AdminPlaceView, PlaceFeedbackKindView, PlaceFeedbackRejectReasonView, PlaceFeedbackStatusView } from '@vello/api-client';
import { draftPoint, placesScopeQuery } from './places.js';
import type { OperatorScope } from './shell.js';

/** The status filter of the queue: one status, or every one. */
export type FeedbackStatusFilter = PlaceFeedbackStatusView | 'all';
export const FEEDBACK_STATUS_FILTERS: readonly FeedbackStatusFilter[] = ['pending', 'accepted', 'rejected', 'merged', 'handled', 'all'];
/** The kinds the queue can be filtered by. */
export const FEEDBACK_KINDS: readonly PlaceFeedbackKindView[] = ['add', 'fix', 'comment'];
export const FEEDBACK_REJECT_REASONS: readonly PlaceFeedbackRejectReasonView[] = ['duplicate', 'not_found', 'not_a_place', 'abuse', 'other'];
export const FEEDBACK_PAGE_SIZE = 50;

/** The queue query for a scope, a status filter, an optional kind and the page. */
export function placeFeedbackQuery(scope: OperatorScope, status: FeedbackStatusFilter, kind: PlaceFeedbackKindView | '', offset: number, limit: number = FEEDBACK_PAGE_SIZE): AdminPlaceFeedbackQuery {
  return { ...placesScopeQuery(scope), ...(status !== 'all' ? { status } : {}), ...(kind !== '' ? { kind } : {}), limit, offset };
}

/** The pending count of the scope (the number on the Sugestões tab): one row asked, the total read. */
export const pendingCountQuery = (scope: OperatorScope): AdminPlaceFeedbackQuery => ({ ...placesScopeQuery(scope), status: 'pending', limit: 1, offset: 0 });

/** The line a row leads with: the name the rider gave, else the address, else the place it is about; a comment leads with its note. */
export const feedbackHeadline = (f: Pick<AdminPlaceFeedbackView, 'kind' | 'name' | 'address' | 'targetLabel' | 'note'>): string =>
  f.kind === 'comment' ? (f.note ?? '').split('\n')[0] ?? '' : (f.name ?? f.address ?? f.targetLabel ?? '');

/** What the operator can do with a pending row: a suggestion about a place is accepted, merged or rejected; a comment about the search is handled or rejected. */
export interface FeedbackActions {
  readonly accept: boolean;
  readonly merge: boolean;
  readonly handle: boolean;
  readonly reject: boolean;
}
export const feedbackActions = (f: Pick<AdminPlaceFeedbackView, 'kind' | 'status'>): FeedbackActions => {
  if (f.status !== 'pending') return { accept: false, merge: false, handle: false, reject: false };
  const comment = f.kind === 'comment';
  return { accept: !comment, merge: !comment, handle: comment, reject: true };
};

/** The base place a correction is about, as the base holds it NOW (undefined when it was not from the base, or no longer exists). */
export const feedbackTargetPlace = (f: Pick<AdminPlaceFeedbackView, 'targetPlaceId'>, places: Readonly<Record<string, AdminPlaceView>>): AdminPlaceView | undefined =>
  f.targetPlaceId === null ? undefined : places[f.targetPlaceId];

/** Accepting this suggestion changes a VERIFIED base place: the server refuses it unless the operator says so explicitly. */
export const acceptNeedsOverride = (f: Pick<AdminPlaceFeedbackView, 'kind' | 'targetPlaceId'>, places: Readonly<Record<string, AdminPlaceView>>): boolean =>
  f.kind === 'fix' && feedbackTargetPlace(f, places)?.verified === true;

/** Accepting this suggestion writes a NEW place (an add, or a correction of a place that is not in the base) - which needs a point. */
export const acceptCreatesPlace = (f: Pick<AdminPlaceFeedbackView, 'kind' | 'targetPlaceId'>): boolean => f.kind === 'add' || (f.kind === 'fix' && f.targetPlaceId === null);

/** What the operator may adjust before accepting (texts as typed, the point as text). */
export interface AcceptDraft {
  readonly name: string;
  readonly address: string;
  readonly kind: string;
  readonly latitude: string;
  readonly longitude: string;
  readonly overrideVerified: boolean;
}

const coord = (n: number): string => String(Math.round(n * 1e6) / 1e6);

/** The accept form starts as the rider sent it (a correction of a place not in the base starts on the place's label and point). */
export function acceptDraftOf(f: AdminPlaceFeedbackView): AcceptDraft {
  const point = f.location ?? (f.kind === 'fix' && f.targetPlaceId === null ? f.targetLocation : null);
  return {
    name: f.name ?? (f.kind === 'fix' && f.targetPlaceId === null ? (f.targetLabel ?? '') : ''),
    address: f.address ?? '',
    kind: '',
    latitude: point === null ? '' : coord(point.latitude),
    longitude: point === null ? '' : coord(point.longitude),
    overrideVerified: false,
  };
}

/** What is still missing before the accept can be sent (`PLACE_FEEDBACK_*` codes the catalog words), or undefined. */
export function acceptDraftIssue(f: AdminPlaceFeedbackView, d: AcceptDraft, places: Readonly<Record<string, AdminPlaceView>>): string | undefined {
  const typedPoint = d.latitude.trim() !== '' || d.longitude.trim() !== '';
  if (typedPoint && draftPoint(d.latitude, d.longitude) === undefined) return 'PLACE_FEEDBACK_LOCATION_INVALID';
  if (acceptCreatesPlace(f) && f.issue !== 'closed' && !typedPoint) return 'PLACE_FEEDBACK_LOCATION_REQUIRED';
  if (d.name.trim().length > 200) return 'PLACE_FEEDBACK_NAME_INVALID';
  if (d.address.trim().length > 400) return 'PLACE_FEEDBACK_ADDRESS_INVALID';
  if (acceptNeedsOverride(f, places) && !d.overrideVerified) return 'PLACE_FEEDBACK_VERIFIED_PROTECTED';
  return undefined;
}

/** The accept body: only what the operator changed from what the rider sent (an empty body accepts it as sent). */
export function acceptBodyOf(f: AdminPlaceFeedbackView, d: AcceptDraft): AdminPlaceFeedbackAccept {
  const start = acceptDraftOf(f);
  const point = draftPoint(d.latitude, d.longitude);
  const pointChanged = d.latitude.trim() !== start.latitude || d.longitude.trim() !== start.longitude;
  const sendPoint = point !== undefined && (pointChanged || f.location === null);
  return {
    ...(d.name.trim() !== '' && d.name.trim() !== start.name.trim() ? { name: d.name.trim() } : {}),
    ...(d.address.trim() !== '' && d.address.trim() !== start.address.trim() ? { address: d.address.trim() } : {}),
    ...(d.kind !== '' ? { kind: d.kind } : {}),
    ...(sendPoint ? { location: point } : {}),
    ...(d.overrideVerified ? { overrideVerified: true } : {}),
  };
}

/** The point a tap on the map sets in the accept form. */
export const withAcceptPoint = (d: AcceptDraft, p: { readonly latitude: number; readonly longitude: number }): AcceptDraft => ({ ...d, latitude: coord(p.latitude), longitude: coord(p.longitude) });
